import { Component, inject } from '@angular/core';
import { UpperCasePipe } from '@angular/common';
import {
    DxButtonComponent,
    DxDataGridComponent,
    DxFormComponent,
    DxTemplateDirective,
} from 'devextreme-angular';
import {
    DxiColumnComponent,
    DxiItemComponent,
    DxoColumnChooserComponent,
    DxoExportComponent,
    DxoFilterRowComponent,
    DxoHeaderFilterComponent,
    DxoLoadPanelComponent,
    DxoPagerComponent,
    DxoPagingComponent,
    DxoPositionComponent,
    DxoScrollingComponent,
    DxoSearchComponent,
    DxoSearchPanelComponent,
    DxoSelectionComponent,
    DxoSortingComponent,
    DxoToolbarComponent,
} from 'devextreme-angular/ui/nested';
import { FilterPanelComponent } from '../../../components/library/filter-panel/filter-panel.component';
import {
    FilterConfig,
    GridPageComponent,
} from '../../common/grid-page.component';
import {
    AccessType,
    AuthService,
    CreditRequestGridViewDto,
    CreditRequestStatus,
    CurrencyCode,
    PermissionType,
} from '@services';
import EnumUtils from '../../../shared/enum-utils';
import { CommonModule, NgClass } from '@angular/common';
import { DxDataGridTypes } from 'devextreme-angular/ui/data-grid';
import { Router, RouterLink } from '@angular/router';
import { DATE_FORMAT } from 'src/app/shared/constants';
import { CreditRequestStatusPipe } from 'src/app/pipes/credit-requests-status.pipe';

@Component({
    selector: 'app-credit-requests',
    imports: [
        CommonModule,
        DxDataGridComponent,
        DxoScrollingComponent,
        DxoPagingComponent,
        DxoPagerComponent,
        DxoLoadPanelComponent,
        DxoPositionComponent,
        DxoSortingComponent,
        DxoSearchComponent,
        DxoHeaderFilterComponent,
        DxoFilterRowComponent,
        DxoColumnChooserComponent,
        DxoSearchPanelComponent,
        DxoSelectionComponent,
        DxoExportComponent,
        DxoToolbarComponent,
        DxiItemComponent,
        DxButtonComponent,
        DxiColumnComponent,
        DxTemplateDirective,
        NgClass,
        FilterPanelComponent,
        DxFormComponent,
        RouterLink,
        CreditRequestStatusPipe,
    ],
    providers: [UpperCasePipe, CreditRequestStatusPipe],
    templateUrl: './credit-requests.component.html',
})
export class CreditRequestsComponent extends GridPageComponent<CreditRequestGridViewDto> {
    private router = inject(Router);
    private authService = inject(AuthService);
    private upperCasePipe = inject(UpperCasePipe);
    private creditRequestStatusPipe = inject(CreditRequestStatusPipe);

    protected override exportName: string = 'Invoice Report';
    protected offset: number = 100;
    public dateFormat = DATE_FORMAT;

    dataSource = this.dataSources.creditRequests();

    statusOptions = EnumUtils.getEnumOptions(CreditRequestStatus, (v) =>
        this.creditRequestStatusPipe.transform(v),
    );

    currencyOptions = EnumUtils.getEnumOptions(CurrencyCode, (v) =>
        this.upperCasePipe.transform(v),
    );

    protected override filterConfig: FilterConfig[] = [
        { field: 'number', op: 'contains' },
        { field: 'invoiceNumber', op: 'contains' },
        { field: 'serviceType', op: 'contains' },
        { field: 'vendor', op: 'contains' },
        { field: 'issueDate', op: '>=' },
        { field: 'dueDate', op: '>=' },
        { field: 'subTotal', op: '<=' },
        { field: 'taxAmount', op: '<=' },
        { field: 'totalAmount', op: '<=' },
        { field: 'convertedSubTotal', op: '<=' },
        { field: 'convertedTaxAmount', op: '<=' },
        { field: 'convertedTotalAmount', op: '<=' },
        { field: 'status', op: '=' },
    ];

    constructor() {
        super();
        this.init(CreditRequestGridViewDto);
    }

    protected getBadgeClass(status: CreditRequestStatus | undefined): string {
        return EnumUtils.getCreditRequestStatusBadgeClass(status);
    }

    addCreditRequest = async () => {
        await this.router.navigate(['/invoicing/credit-requests/create']);
    };

    async onRowDblClick(e: DxDataGridTypes.RowDblClickEvent) {
        await this.router.navigate([
            `/invoicing/credit-requests/details/${e.data.number}`,
        ]);
    }

    get canCreate(){
        return this.authService.checkPermission(PermissionType.CreditRequests, AccessType.Edit);
    }
}